import { useEffect, useRef, useState } from "react";
import { RotateCcw, Pause, Play, ZoomIn, ZoomOut } from "lucide-react";
import { img } from "../lib/api";
import Product3D from "./Product3D";
import { insideOk } from "../lib/categories";

export default function Viewer3D({ images, product }) {
  if (product?.views?.front) return <Product3D views={insideOk(product.category, product.subcategory) ? product.views : { ...product.views, inside: undefined }} dimensions={product.dimensions} />;
  return <FlatViewer images={images} />;
}

function FlatViewer({ images }) {
  const [rot, setRot] = useState({ x: -8, y: -25 });
  const [auto, setAuto] = useState(true);
  const [zoom, setZoom] = useState(1);
  const drag = useRef(null);
  const front = img(images?.[0]);
  const back = front;

  useEffect(() => {
    if (!auto) return;
    let id;
    const tick = () => { setRot((r) => ({ ...r, y: r.y + 0.35 })); id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [auto]);

  const down = (e) => { setAuto(false); drag.current = { x: e.clientX, y: e.clientY, r: rot }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setRot({ y: d.r.y + (e.clientX - d.x) * 0.5, x: Math.max(-40, Math.min(40, d.r.x - (e.clientY - d.y) * 0.3)) });
  };
  const face = "absolute inset-0 rounded-2xl overflow-hidden border border-white/10 [backface-visibility:hidden]";

  return (
    <div data-testid="product-3d-viewer" className="relative aspect-square rounded-3xl bg-[#12141c] border border-white/10 overflow-hidden select-none hero-radial">
      <div className="absolute inset-0 grid place-items-center cursor-grab active:cursor-grabbing touch-none [perspective:1100px]"
        onPointerDown={down} onPointerMove={move} onPointerUp={() => (drag.current = null)}>
        <div className="relative w-[62%] aspect-[4/5] [transform-style:preserve-3d]"
          style={{ transform: `scale(${zoom}) rotateX(${rot.x}deg) rotateY(${rot.y}deg)` }}>
          <div className={face} style={{ transform: "translateZ(14px)" }}><img src={front} alt="front" className="w-full h-full object-cover" draggable={false} /></div>
          <div className={face} style={{ transform: "rotateY(180deg) translateZ(14px)" }}><img src={back} alt="back" className="w-full h-full object-cover -scale-x-100 brightness-90" draggable={false} /></div>
        </div>
        <div className="absolute bottom-[8%] w-1/2 h-6 rounded-[50%] bg-black/70 blur-xl" />
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 backdrop-blur-xl rounded-full p-1.5 border border-white/10">
        <button data-testid="viewer-autorotate-toggle" onClick={() => setAuto((a) => !a)} className="p-2 rounded-full hover:bg-white/10 transition-colors">{auto ? <Pause size={16} /> : <Play size={16} />}</button>
        <button data-testid="viewer-zoom-in" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))} className="p-2 rounded-full hover:bg-white/10 transition-colors"><ZoomIn size={16} /></button>
        <button data-testid="viewer-zoom-out" onClick={() => setZoom((z) => Math.max(0.7, z - 0.1))} className="p-2 rounded-full hover:bg-white/10 transition-colors"><ZoomOut size={16} /></button>
        <button data-testid="viewer-reset" onClick={() => { setRot({ x: -8, y: -25 }); setZoom(1); }} className="p-2 rounded-full hover:bg-white/10 transition-colors"><RotateCcw size={16} /></button>
      </div>
      <p className="absolute top-4 left-4 text-[11px] uppercase tracking-widest text-[#00f0ff]">Drag to rotate · 360°</p>
    </div>
  );
}
